/**
 * SizersLayout — the chrome around the sizers (`/sizers/*`).
 *
 *   ┌───────────────────────────────────────────────────────┐
 *   │ Priced Scenarios  RTL Sizer  GUC Sizer  DSCR …        │ ← sub-tabs, STICKY
 *   ├───────────────────────────────────────────────────────┤
 *   │ <Outlet/>                                             │
 *   └───────────────────────────────────────────────────────┘
 *
 * AdminLayout's main column without the admin sidebar or the breadcrumb (the
 * tab strip already says where you are; inside a scenario, "← All scenarios"
 * does the breadcrumb's job): the sizers moved out of
 * Admin so non-admins in the "Sizer Visibility" group can reach them, and an
 * Admin sidebar would advertise destinations those users can't open.
 *
 * Like AdminLayout, SCROLLING lives here in the main column, not in the
 * screens — `SizerShell` deliberately opens no scroller, and its sticky pieces
 * and `scrollSizerToTop` resolve against this column.
 *
 * The gate is re-checked here (not only in the header nav) because a route is
 * reachable by typing it.
 */
import { useMemo } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import Box from "@mui/material/Box";

import { useCanSeeSizers } from "../hooks/useCanSeeSizers";
import { A } from "../screens/admin/shared/adminTokens";
import SectionSubTabs from "./SectionSubTabs";
import { useCanUseDscrSizer } from "../hooks/useCanUseDscrSizer";
import { visibleSizerTabs } from "./sizersNav";

export default function SizersLayout() {
  const location = useLocation();
  const { allowed, loading } = useCanSeeSizers();
  const canUseDscr = useCanUseDscrSizer();
  const tabs = useMemo(() => visibleSizerTabs({ canUseDscr }), [canUseDscr]);

  // Falls back to the first tab so the bare `/sizers` frame (before its
  // redirect lands) still marks one.
  const activeTab = useMemo(
    () => tabs.find((s) => location.pathname.startsWith(s.to)) || tabs[0],
    [location.pathname, tabs],
  );

  // Nothing until the group lookup settles — a sizer that flashes in and then
  // redirects away is worse than one that arrives a beat late.
  if (loading) return null;
  if (!allowed) return <Navigate to="/loans" replace />;

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        height: "100%",
        overflowY: "auto",
        overflowX: "hidden",
        bgcolor: A.bg,
      }}
    >
      <SectionSubTabs variant="page" tabs={tabs} activeTo={activeTab.to} />

      <Outlet />
    </Box>
  );
}
